import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getFreeRoundsCampaignData,
    getPromotionsErrorData,
    getFreeRoundsCampaignPlayersData,
    getFreeRoundsCampaignPlayersTotalData,
    shouldUpdateData,
    isExportingPlayersCsvData,
} from '../../../../redux/selectors/backoffice/propmotions-selectors';
import {
    clearCampaignData,
    getFreeRoundsCampaign,
    getFreeRoundsCampaignPlayers,
    addPlayerToCampaign,
    removePlayersFromCampaign,
    importPlayersCsv,
    exportPlayersCsv,
} from '../../../../redux/actions/backoffice/promotions-actions';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorComponent } from '../../../../Containers/Errors/ErrorComponent';
import { Loader } from '../../../../Components/Loader/Loader';
import { Table, Button, Input, Select, Space, Modal, Upload, Tag } from 'antd';
import {
    DeleteOutlined, 
    PlusOutlined, 
    UploadOutlined, 
    DownloadOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    MinusCircleOutlined,
    ArrowLeftOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { canManagePromotionCampaign } from '../../promotionUtils';
import { canAccessPlayerProfile, Messages } from '../../../../utils';

const { Search } = Input;

const DEFAULT_PAGINATION = { pageNumber: 1, pageSize: 20 };
type SortOrder = 'DESCENDING' | 'ASCENDING';
const DEFAULT_SORT_CONFIG = { sortKey: 'id', sortOrder: 'DESCENDING' as SortOrder };
const shouldLinkToPlayerProfile = canAccessPlayerProfile();

const columns = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 90,
    },
    {
        title: 'Player ID',
        dataIndex: 'playerId',
        key: 'playerId',
        render: (text: string) =>
            shouldLinkToPlayerProfile ? (
                <Link to={`/casino/players/null/${text}`}>{text}</Link>
            ) : (
                <span>{text}</span>
            ),
        sorter: true,
        width: 150,
    },
    {
        title: 'Received At',
        dataIndex: 'receivedAt',
        key: 'receivedAt',
        sorter: true,
        width: 200,
    },
    {
        title: () => (
            <span className="table-title__span">
                Total Bet
                <span>USD</span>
            </span>
        ),
        dataIndex: 'totalBet',
        key: 'totalBet',
        sorter: true,
        width: 130,
    },
    {
        title: () => (
            <span className="table-title__span">
                Total Win
                <span>USD</span>
            </span>
        ),
        dataIndex: 'totalWin',
        key: 'totalWin',
        sorter: true,
        width: 130,
    },
    {
        title: 'Played Features',
        dataIndex: 'roundsPlayed',
        key: 'roundsPlayed',
        width: 130,
    },
    {
        title: 'Last Round Date',
        dataIndex: 'lastRoundDate',
        key: 'lastRoundDate',
        width: 200,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (text: string) => {
            if (text === 'QUEUED') {
                return <Tag icon={<ClockCircleOutlined />} color="processing">Queued</Tag>;
            } else if (text === 'COMPLETED') {
                return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
            } else if (text === 'IN_USE') {
                return <Tag icon={<PlayCircleOutlined />} color="default">In Use</Tag>;
            } else if (text === 'CANCELLED') {
                return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled</Tag>;
            } else if (text === 'EXPIRED') {
                return <Tag icon={<MinusCircleOutlined />} color="warning">Expired</Tag>;
            }
            return null;
        }
    },
];

const ManagePlayersInFreeRoundsCampaign = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const campaignData = useSelector(getFreeRoundsCampaignData);
    const error = useSelector(getPromotionsErrorData);
    const players = useSelector(getFreeRoundsCampaignPlayersData);
    const playersTotal = useSelector(getFreeRoundsCampaignPlayersTotalData);
    const shouldUpdate = useSelector(shouldUpdateData);
    const isExporting = useSelector(isExportingPlayersCsvData);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
    const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
    const [isAddPlayerModalVisible, setIsAddPlayerModalVisible] = useState(false);
    const [newPlayerId, setNewPlayerId] = useState('');
    const [tableHeight, setTableHeight] = useState(0);
    const totalWidth: number = columns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight - 400;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const [confirmModal, setConfirmModal] = useState<{
        visible: boolean;
        title: string;
        content: string;
        onConfirm: () => void;
    }>({ visible: false, title: '', content: '', onConfirm: () => {} });

    useEffect(() => {
        if (campaignData && !canManagePromotionCampaign(campaignData)) {
            navigate(`/casino/free-rounds-promotion/${params.campaignId}`);
        }
    }, [campaignData, navigate, params.campaignId]);

    const handleConfirm = () => {
        confirmModal.onConfirm();
        setConfirmModal({ ...confirmModal, visible: false });
    };

    const uploadProps = {
        name: 'file',
        accept: '.csv',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file: File) => {
            const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
            if (!isCSV) {
                Messages.error('Invalid file type. Please upload a CSV file.');
                return false;
            }
            setConfirmModal({
                visible: true,
                title: 'Confirm CSV Import',
                content: `Are you sure you want to import the file: ${file.name}?`,
                onConfirm: () => {
                    dispatch(importPlayersCsv({ file, campaignId: campaignData?.id }));
                },
            });
            return false;
        },
    };

    const handleRemoveSelectedPlayers = () => {
        setConfirmModal({
            visible: true,
            title: 'Confirm Removal',
            content: `Are you sure you want to remove ${selectedRowKeys.length} selected players?`,
            onConfirm: () => {
                dispatch(
                    removePlayersFromCampaign({
                        campaignId: campaignData?.id,
                        playerIds: selectedRowKeys,
                    }),
                );
                setSelectedRowKeys([]);
            },
        });
    };

    const handleAddPlayer = () => {
        if (!newPlayerId.trim()) {
            return;
        }

        setIsAddPlayerModalVisible(false);

        setConfirmModal({
            visible: true,
            title: 'Confirm Add Player',
            content: `Are you sure you want to add Player ID: ${newPlayerId}?`,
            onConfirm: () => {
                dispatch(
                    addPlayerToCampaign({ playerId: newPlayerId, campaignId: campaignData?.id }),
                );
                setIsAddPlayerModalVisible(false);
                setNewPlayerId('');
            },
        });
    };

    const handleExport = () => {
        dispatch(
            exportPlayersCsv({
                campaignId: campaignData?.id,
                players: players || [],
                paginationData: {
                    pageNumber: 1,
                    pageSize: playersTotal || paginationData.pageSize,
                },
                sortKey: sortConfig.sortKey,
                sortOrder: sortConfig.sortOrder,
                playerIdSearchValue: searchQuery || null,
                playersStatus: statusFilter,
                currency: 'USD',
            }),
        );
    };

    const requestCampaign = useCallback(() => {
        if (params.campaignId) {
            dispatch(
                getFreeRoundsCampaign({
                    campaignId: params.campaignId,
                    currency: 'USD',
                }),
            );
        }
    }, [dispatch, params.campaignId]);

    const fetchPlayers = useCallback(() => {
        if (campaignData?.id) {
            dispatch(
                getFreeRoundsCampaignPlayers({
                    campaignId: campaignData.id,
                    paginationData,
                    sortKey: sortConfig.sortKey,
                    sortOrder: sortConfig.sortOrder,
                    playerIdSearchValue: searchQuery || null,
                    playersStatus: statusFilter,
                    currency: 'USD',
                }),
            );
        }
    }, [dispatch, campaignData?.id, paginationData, sortConfig, searchQuery, statusFilter]);

    useEffect(() => {
        requestCampaign();
    }, [requestCampaign]);

    useEffect(() => {
        fetchPlayers();
    }, [fetchPlayers]);

    useEffect(() => {
        if (shouldUpdate) {
            fetchPlayers();
        }
    }, [fetchPlayers, shouldUpdate]);

    useEffect(() => {
        return () => {
            dispatch(clearCampaignData());
        };
    }, [dispatch]);

    const handleStatusChange = (status: string) => {
        setStatusFilter(status);
        setPaginationData({ ...paginationData, pageNumber: 1 });
    };

    const handlePagination = (page: number, pageSize?: number) => {
        setPaginationData({ pageNumber: page, pageSize: pageSize || DEFAULT_PAGINATION.pageSize });
    };

    const handleSort = (sorter: any) => {
        if (Array.isArray(sorter) && sorter.length && sorter[0].order) {
            setSortConfig({
                sortKey: sorter[0].field?.toString() || 'id',
                sortOrder: sorter[0].order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        } else if (!Array.isArray(sorter) && sorter.field && sorter.order) {
            setSortConfig({
                sortKey: sorter.field.toString(),
                sortOrder: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        }
    };

    const onTableChange = (pagination: any, filters: any, sorter: any) => {
        handlePagination(pagination.current ?? 1, pagination.pageSize);
        handleSort(sorter);
    };

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col px-6 pb-4">
            <div className="flex justify-between items-center pt-4 pb-4">
                <div className="flex items-center gap-2">
                    <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate(-1)} className="p-6 font-medium" />
                    <div className="">
                        <div className="text-xs text-gray-400 mb-1">
                            <Link to="/casino/free-rounds-promotions" className="hover:underline text-gray-400">Promotions</Link>
                        </div>
                        <h1 className="text-2xl font-semibold text-gray-900">{campaignData?.name || ''}</h1>
                    </div>
                    
                </div>
            </div>
            <div className="flex flex-col bg-white rounded-lg border border-gray-200 px-6 py-4 h-full">
                <Modal
                    title={confirmModal.title}
                    open={confirmModal.visible}
                    onCancel={() => setConfirmModal({ ...confirmModal, visible: false })}
                    footer={[
                        <Button
                            key="cancel"
                            onClick={() => setConfirmModal({ ...confirmModal, visible: false })}
                        >
                            Cancel
                        </Button>,
                        <Button key="confirm" type="primary" onClick={handleConfirm}>
                            Confirm
                        </Button>,
                    ]}
                >
                    <p>{confirmModal.content}</p>
                </Modal>

                <Modal
                    title="Add Player"
                    open={isAddPlayerModalVisible}
                    onCancel={() => {
                        setNewPlayerId('');
                        setIsAddPlayerModalVisible(false);
                    }}
                    footer={[
                        <Button
                            key="cancel"
                            onClick={() => {
                                setNewPlayerId('');
                                setIsAddPlayerModalVisible(false);
                            }}
                        >
                            Cancel
                        </Button>,
                        <Button key="submit" type="primary" onClick={handleAddPlayer}>
                            Add Player
                        </Button>,
                    ]}
                >
                    <label htmlFor="playerId">Player ID</label>
                    <Input
                        id="playerId"
                        size="large"
                        value={newPlayerId}
                        onChange={(e) => setNewPlayerId(e.target.value)}
                        placeholder="Enter Player ID"
                    />
                </Modal>

                {/* Top bar */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        placeholder="Status"
                        style={{ width: 120 }}
                        className="mr-2"
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'QUEUED', label: 'Queued' },
                            { value: 'IN_USE', label: 'In Use' },
                            { value: 'OPT_OUT', label: 'Opted Out' },
                            { value: 'COMPLETED', label: 'Completed' },
                            { value: 'EXPIRED', label: 'Expired' },
                        ]}
                    />
                    <Search
                        placeholder="Search players"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: 240 }}
                        className="mr-2"
                        allowClear
                    />
                    <div className="flex-1" />
                    <Space>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />}>
                                Import CSV
                            </Button>
                        </Upload>
                        <Button icon={<DownloadOutlined />} onClick={handleExport} loading={isExporting}>
                            Export to CSV
                        </Button>
                    </Space>
                </div>

                {/* Add Player Button */}
                <div className="mb-2">
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => setIsAddPlayerModalVisible(true)}>
                        Add Player
                    </Button>
                </div>

                {/* Table */}
                {error ? (
                    <ErrorComponent error={error} />
                ) : campaignData ? (
                    <>
                        {selectedRowKeys.length > 0 && (
                            <div className="mb-4">
                                <Button
                                    onClick={handleRemoveSelectedPlayers}
                                    icon={<DeleteOutlined />}
                                    danger
                                >
                                    Remove Selected ({selectedRowKeys.length})
                                </Button>
                            </div>
                        )}
                        <Table
                            virtual
                            size="small"
                            columns={columns}
                            dataSource={players || []}
                            rowKey="playerId"
                            rowSelection={{
                                columnWidth: 32,
                                selectedRowKeys,
                                onChange: (keys) => setSelectedRowKeys(keys),
                                getCheckboxProps: (record) => ({
                                    disabled: record.status !== 'QUEUED',
                                }),
                            }}
                            pagination={{
                                current: paginationData.pageNumber,
                                pageSize: paginationData.pageSize,
                                total: playersTotal,
                                showSizeChanger: true,
                                onChange: handlePagination,
                            }}
                            onChange={onTableChange}
                            scroll={{ y: tableHeight, x: totalWidth + 70 }}
                            className="border-0"
                            bordered={false}
                            rowClassName="border-b border-gray-100 last:border-b-0"
                        />
                    </>
                ) : (
                    <Loader className="h-[60vh]" />
                )}
            </div>
        </div>
    );
};

export default ManagePlayersInFreeRoundsCampaign;
